import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { lineStringSchema } from "../geojson";
import { paginatedSchema } from "../pagination";

import { baseAssetSchema } from "./assetsBase";

export const roadListItemSchema = baseAssetSchema.extend({
  geometry: lineStringSchema,
  name: z.string(),
  type: z.string().nullable(),
  use: z.string().nullable(),
  region: z.string().nullable() /* URL of Administrative Boundary */,
  start: lizardDateSchema,
});

export type RoadListItem = z.infer<typeof roadListItemSchema>;

export const roadListSchema = paginatedSchema(roadListItemSchema);
export type RoadList = z.infer<typeof roadListSchema>;

export const roadSchema = roadListItemSchema;
export type Road = z.infer<typeof roadSchema>;

export function useRoads(params = {}) {
  const url = combineUrlAndParams("/api/v4/roads/", params);
  return useParsedQuery<RoadList>(
    ["lizard-api-v4", "roads", url],
    () => fetchWithError(url),
    (json: unknown) => roadListSchema.parse(json)
  );
}

export function useRoad(id: number) {
  return useParsedQuery<Road>(
    ["lizard-api-v4", "roads", id],
    () => fetchWithError(`/api/v4/roads/${id}/`),
    (json: unknown) => roadSchema.parse(json)
  );
}

export function useMultipleRoads(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "roads", id],
        fetchFunction: () => fetchWithError(`/api/v4/roads/${id}/`),
        parseFunction: (json: unknown) => roadSchema.parse(json),
      };
    })
  );
}
